package com.gloopgo.game.network

/**
 * Blocks nicknames that contain common English profanity (including simple leetspeak).
 * Matching is case-insensitive on alphanumeric tokens and the compacted letter stream.
 * Length limits are enforced separately via [com.gloopgo.game.engine.GameConstants].
 */
object NicknameValidator {
    private val blocked = setOf(
        "ANAL", "ANUS", "ARSE", "ASS", "ASSHOLE", "BALLS", "BASTARD", "BITCH",
        "BLOWJOB", "BOLLOCK", "BONER", "BOOB", "BUGGER", "BULLSHIT", "BUTT",
        "BUTTHOLE", "CLIT", "COCK", "COON", "CRAP", "CUM", "CUNT", "DAMN",
        "DICK", "DILDO", "DYKE", "FAG", "FAGGOT", "FELTCH", "FEMDOM", "FUCK",
        "FUK", "GODDAMN", "HANDJOB", "HOMO", "HOOKER", "HORNY", "JIZZ", "KIKE",
        "LABIA", "LESBO", "MOTHERFUCKER", "MUFF", "NIGGA", "NIGGER", "ORGY",
        "PENIS", "PISS", "POON", "PORN", "PRICK", "PUBE", "PUSSY", "QUEER",
        "RAPE", "RAPIST", "RECTUM", "RETARD", "SCROTUM", "SEX", "SHIT", "SLUT",
        "SMEGMA", "SPUNK", "SUCK", "TIT", "TITS", "TURD", "TWAT", "VAGINA",
        "VULVA", "WANK", "WHORE", "WTF",
    )

    /** True when [nickname] contains a blocked term (empty input is not considered blocked). */
    fun containsBlockedTerm(nickname: String): Boolean {
        val trimmed = nickname.trim()
        if (trimmed.isEmpty()) return false
        val upper = trimmed.uppercase()
        val normalized = normalizeLeet(upper)
        val compacted = normalized.filter { it.isLetterOrDigit() }
        val tokens = normalized.split(Regex("[^A-Z0-9]+")).filter { it.isNotEmpty() }
        val candidates = tokens + listOf(compacted)
        for (word in blocked) {
            if (candidates.any { it == word || it.contains(word) }) {
                // Avoid common false positives for short stems inside longer clean words.
                if (word.length <= 3) {
                    if (tokens.any { it == word } || compacted == word) return true
                    continue
                }
                return true
            }
        }
        return false
    }

    /** True when [nickname] is non-blank and does not contain blocked terms. */
    fun isAllowed(nickname: String): Boolean {
        val trimmed = nickname.trim()
        if (trimmed.isEmpty()) return false
        return !containsBlockedTerm(trimmed)
    }

    private fun normalizeLeet(value: String): String =
        buildString(value.length) {
            for (ch in value) {
                append(
                    when (ch) {
                        '0' -> 'O'
                        '1' -> 'I'
                        '3' -> 'E'
                        '4' -> 'A'
                        '5' -> 'S'
                        '7' -> 'T'
                        '@' -> 'A'
                        '$' -> 'S'
                        else -> ch
                    }
                )
            }
        }
}
